<template>
  <div class="boosting-invitations">
    <div class="subsection-header">
      <i class="material-icons subsection-icon">send</i>
      <span>{{ $t('dash.boosting.invitations.title') }}</span>
    </div>

    <div class="invitation-tabs">
      <div class="ctab" :class="{ active: tab === 'pending' }" @click="tab = 'pending'">
        {{ $t('dash.boosting.invitations.tab_pending') }} ({{ lists.pending.total }})
      </div>
      <div class="ctab" :class="{ active: tab === 'invited' }" @click="tab = 'invited'">
        {{ $t('dash.boosting.invitations.tab_invited') }} ({{ lists.invited.total }})
      </div>
      <div class="ctab" :class="{ active: tab === 'subscribed' }" @click="tab = 'subscribed'">
        {{ $t('dash.boosting.invitations.tab_subscribed') }} ({{ lists.subscribed.total }})
      </div>
    </div>

    <div class="search-input-wrap">
      <i class="material-icons search-icon">search</i>
      <input
        class="search-input"
        type="text"
        v-model="search"
        :placeholder="$t('dash.boosting.invitations.search_placeholder')"
        @input="onSearchInput"
        @keydown.escape="clearSearch"
      >
      <div class="input-loader" v-if="current.loading && search"></div>
      <i class="material-icons search-clear" v-else-if="search" @click="clearSearch">close</i>
    </div>

    <p class="disabled-hint" v-if="!boostingEnabled">
      <i class="material-icons">info_outline</i>
      {{ $t('dash.boosting.invitations.disabled') }}
    </p>

    <p class="schedule-hint" v-if="tab === 'pending' && boostingEnabled && schedule.next_invite_at && lists.pending.total">
      <i class="material-icons">schedule</i>
      {{ $t('dash.boosting.invitations.next_invite', { date: formatDateTime(schedule.next_invite_at) }) }}
      <span class="schedule-quota" v-if="!global">· {{ $t('dash.boosting.invitations.sent_today', { count: schedule.invited_today, cap: schedule.daily_cap }) }}</span>
    </p>

    <p class="schedule-hint" v-if="tab === 'invited' && boostingEnabled && schedule.next_auto_check_at && lists.invited.total">
      <i class="material-icons">schedule</i>
      {{ $t('dash.boosting.invitations.next_auto_check', { date: formatDateTime(schedule.next_auto_check_at) }) }}
    </p>

    <div class="selection-bar" v-if="selectable && current.users.length">
      <label class="select-all">
        <input type="checkbox" :checked="allSelected" :disabled="!boostingEnabled || !selectableUsers.length" @change="toggleAll">
        {{ $t('dash.boosting.invitations.select_all') }}
      </label>
      <button
        v-if="tab === 'pending'"
        class="action-btn action-btn--primary"
        :disabled="!boostingEnabled || !selected.length || bulkLoading || processing"
        @click="inviteSelected"
      >
        <i class="material-icons" :class="{ spin: bulkLoading }">{{ bulkLoading ? 'hourglass_empty' : 'send' }}</i>
        {{ $t('dash.boosting.invitations.invite_selection', { count: selected.length }) }}
      </button>
      <button
        v-else
        class="action-btn action-btn--primary"
        :disabled="!boostingEnabled || !selected.length"
        @click="checkSelected"
      >
        <i class="material-icons">done_all</i>
        {{ $t('dash.boosting.invitations.check_selection', { count: selected.length }) }}
      </button>
    </div>

    <div class="post-empty" v-if="current.loading && !current.users.length">
      <i class="material-icons spin">hourglass_empty</i>
    </div>

    <div class="post-empty" v-else-if="!current.users.length">
      <i class="material-icons">{{ appliedSearch ? 'search' : emptyIcons[tab] }}</i>
      <p v-if="appliedSearch">{{ $t('dash.boosting.invitations.no_result', { query: appliedSearch }) }}</p>
      <p v-else>{{ $t('dash.boosting.invitations.empty_' + tab) }}</p>
    </div>

    <div class="subscriber-list" v-else>
      <div class="subscriber-row" v-for="user in current.users" :key="user.user_id">
        <input
          v-if="selectable"
          type="checkbox"
          :value="user.user_id"
          v-model="selected"
          :disabled="!actionableEntries(user).length || !!queued[user.user_id] || !!waiting[user.user_id] || !!busy[user.user_id] || (selected.length >= bulkMax && !selected.includes(user.user_id))"
        >
        <span class="subscriber-avatar">{{ (user.user_name || '?')[0].toUpperCase() }}</span>
        <BoostingPageAvatars v-if="global" :pages="pagesFor(user)" />
        <div class="subscriber-info">
          <a
            v-if="user.user_url"
            class="subscriber-name"
            :href="user.user_url"
            target="_blank"
            rel="noopener noreferrer"
          >{{ user.user_name || user.user_id }}</a>
          <span v-else class="subscriber-name">{{ user.user_name || user.user_id }}</span>
          <span class="subscriber-meta" v-if="tab === 'pending'">
            {{ $t('dash.boosting.invitations.discovered_on', { date: formatDate(user.discovered_at) }) }}
          </span>
          <span class="subscriber-meta" v-else-if="tab === 'invited'">
            {{ $t('dash.boosting.invitations.invited_on', { date: formatDate(user.invited_at) }) }}
            <template v-if="user.next_check_at">
              · {{ $t('dash.boosting.invitations.next_check', { date: formatDate(user.next_check_at) }) }}
            </template>
          </span>
          <span class="subscriber-meta" v-else>
            {{ $t('dash.boosting.invitations.subscribed_on', { date: formatDate(user.subscribed_at) }) }}
            <template v-if="user.invited_at">
              · {{ $t('dash.boosting.invitations.invited_on', { date: formatDate(user.invited_at) }) }}
            </template>
          </span>
        </div>
        <span class="type-badge badge-pending" v-if="queued[user.user_id] || waiting[user.user_id]">{{ $t('dash.boosting.invitations.queued') }}</span>
        <Tooltip v-if="tab === 'pending' && user.last_error" :text="user.last_error" position="top">
          <span class="type-badge badge-error">{{ $t('dash.boosting.invitations.last_error') }}</span>
        </Tooltip>
        <span class="type-badge badge-cache" v-if="tab === 'invited'">
          {{ $t('dash.boosting.invitations.checks', { count: user.check_count || 0 }) }}
        </span>
        <a
          v-if="user.post_id"
          class="subscriber-post"
          :href="`https://www.facebook.com/${user.post_id}`"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('dash.boosting.view_post') }}
          <i class="material-icons">open_in_new</i>
        </a>
        <button
          v-if="tab === 'pending'"
          class="action-btn"
          :disabled="!actionableEntries(user).length || !!busy[user.user_id] || !!waiting[user.user_id] || !!queued[user.user_id]"
          @click="enqueue('invite', user)"
        >
          <i class="material-icons" :class="{ spin: busy[user.user_id] }">{{ busy[user.user_id] ? 'hourglass_empty' : 'send' }}</i>
          {{ $t('dash.boosting.invitations.invite') }}
        </button>
        <button
          v-else-if="tab === 'invited'"
          class="action-btn"
          :disabled="!actionableEntries(user).length || !!busy[user.user_id] || !!waiting[user.user_id]"
          @click="enqueue('check', user)"
        >
          <i class="material-icons" :class="{ spin: busy[user.user_id] }">{{ busy[user.user_id] ? 'hourglass_empty' : 'done_all' }}</i>
          {{ $t('dash.boosting.invitations.check') }}
        </button>
      </div>

      <button
        v-if="current.users.length < current.total"
        class="page-btn see-more"
        :disabled="current.loading"
        @click="load(tab, true)"
      >
        {{ $t('dash.boosting.invitations.see_more') }}
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Tooltip from '../tooltip/tooltipInit.vue'
import BoostingPageAvatars from './BoostingPageAvatars.vue'

const PAGE_SIZE = 20
const STOP_ALL_ERRORS = ['dash.error.boosting.busy', 'dash.error.boosting.disconnected', 'dash.error.boosting.disabled', 'dash.error.boosting.not_connected']
const STOP_INVITE_ERRORS = ['dash.error.boosting.daily_cap']
const ACTIONABLE_STATUSES = { pending: ['pending'], invited: ['invited', 'expired'] }

export default {
  name: 'BoostingInvitations',
  components: { Tooltip, BoostingPageAvatars },
  props: {
    pages:  { type: Array, required: true },
    global: { type: Boolean, default: false },
  },
  data() {
    return {
      tab: 'pending',
      lists: {
        pending: { users: [], total: 0, loading: false, requestId: 0 },
        invited: { users: [], total: 0, loading: false, requestId: 0 },
        subscribed: { users: [], total: 0, loading: false, requestId: 0 },
      },
      emptyIcons: { pending: 'group_add', invited: 'done_all', subscribed: 'group' },
      schedule: { next_invite_at: null, invited_today: 0, daily_cap: 0, next_auto_check_at: null },
      search: '',
      appliedSearch: '',
      searchDebounce: null,
      boostingEnabled: true,
      enabledPageIds: [],
      selection: { pending: [], invited: [] },
      busy: {},
      queued: {},
      waiting: {},
      actionQueue: [],
      processing: false,
      destroyed: false,
      bulkLoading: false,
      bulkMax: 50,
    }
  },
  computed: {
    current() { return this.lists[this.tab] },
    pageIds() { return this.pages.map(p => p.id) },
    dateFormat() {
      return this.$i18n.locale === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'
    },
    selectable() {
      return this.tab in this.selection
    },
    selected: {
      get() { return this.selection[this.tab] || [] },
      set(value) { if (this.selectable) this.selection[this.tab] = value },
    },
    selectableUsers() {
      if (!this.selectable) return []
      return this.current.users
        .filter(u => this.actionableEntries(u).length && !this.queued[u.user_id] && !this.waiting[u.user_id] && !this.busy[u.user_id])
        .slice(0, this.bulkMax)
    },
    allSelected() {
      return this.selectableUsers.length > 0 && this.selectableUsers.every(u => this.selected.includes(u.user_id))
    },
  },
  mounted() {
    this.loadAll()
  },
  beforeDestroy() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce)
    this.destroyed = true
    this.actionQueue = []
  },
  methods: {
    async api(method, path, body) {
      var token = localStorage.getItem('token')
      var res = await fetch(process.env.VUE_APP_API_HOST + path, {
        method,
        headers: {
          Authorization: token ? ('Bearer ' + token) : '',
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      })
      var json = await res.json().catch(() => ({}))
      return { ok: res.ok, json }
    },

    async load(status, append) {
      var list = this.lists[status]
      var requestId = ++list.requestId
      list.loading = true
      try {
        var from = append ? list.users.length : 0
        var query = '?page_ids=' + this.pageIds.join(',') + '&status=' + status + '&from=' + from + '&size=' + PAGE_SIZE
        if (this.appliedSearch) query += '&search=' + encodeURIComponent(this.appliedSearch)
        var { ok, json } = await this.api('GET', '/reporting/boosting/users' + query)
        if (requestId !== list.requestId) return
        if (!ok) return this.notifyError(json)
        list.users = append ? list.users.concat(json.users || []) : (json.users || [])
        list.total = json.total || 0
        this.boostingEnabled = json.boosting_enabled !== false
        this.enabledPageIds = json.enabled_pages || []
        if (status === 'pending') {
          this.schedule.next_invite_at = json.next_invite_at || null
          this.schedule.invited_today = json.invited_today || 0
          this.schedule.daily_cap = json.daily_cap || 0
        }
        if (status === 'invited') this.schedule.next_auto_check_at = json.next_auto_check_at || null
      } catch (e) {
        if (requestId === list.requestId) this.notifyError({})
      } finally {
        if (requestId === list.requestId) list.loading = false
      }
    },

    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => this.applySearch(), 400)
    },

    applySearch() {
      var query = this.search.trim()
      if (query === this.appliedSearch) return
      this.appliedSearch = query
      this.clearSelection()
      this.loadAll()
    },

    loadAll() {
      Object.keys(this.lists).forEach(status => this.load(status))
    },

    clearSearch() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.search = ''
      this.applySearch()
    },

    removeUser(status, userId) {
      var list = this.lists[status]
      var before = list.users.length
      list.users = list.users.filter(u => u.user_id !== userId)
      if (list.users.length < before) list.total = Math.max(0, list.total - 1)
      this.unselect(userId)
    },

    unselect(userId) {
      Object.keys(this.selection).forEach(status => {
        this.selection[status] = this.selection[status].filter(id => id !== userId)
      })
    },

    clearSelection() {
      Object.keys(this.selection).forEach(status => { this.selection[status] = [] })
    },

    pagesFor(user) {
      var ids = (user.pages || []).map(p => p.page_id)
      return this.pages.filter(p => ids.includes(p.id))
    },

    actionableEntries(user, tab) {
      var statuses = ACTIONABLE_STATUSES[tab || this.tab] || []
      return (user.pages || []).filter(p => statuses.includes(p.status) && this.enabledPageIds.includes(p.page_id))
    },

    applyCheckResult(user, pageId, doc) {
      if (!doc) return
      if (doc.status === 'expired') return this.resolveEntry('invited', user, pageId)
      var fields = { status: doc.status, check_count: doc.check_count, next_check_at: doc.next_check_at }
      var entry = (user.pages || []).find(p => p.page_id === pageId)
      if (entry) Object.assign(entry, fields)
      if (!user.page_id || user.page_id === pageId) Object.keys(fields).forEach(key => this.$set(user, key, fields[key]))
    },

    resolveEntry(status, user, pageId) {
      user.pages = (user.pages || []).filter(p => p.page_id !== pageId)
      if (!this.actionableEntries(user, status).length) this.removeUser(status, user.user_id)
    },

    enqueue(type, user, batch) {
      if (this.busy[user.user_id] || this.waiting[user.user_id]) return 0
      var entries = this.actionableEntries(user, type === 'invite' ? 'pending' : 'invited')
      if (!entries.length) return 0
      this.$set(this.waiting, user.user_id, entries.length)
      this.unselect(user.user_id)
      entries.forEach(entry => this.actionQueue.push({ type, user, pageId: entry.page_id, batch }))
      this.runQueue()
      return entries.length
    },

    checkSelected() {
      var ids = this.selection.invited.slice(0, this.bulkMax)
      var users = this.lists.invited.users.filter(u => ids.includes(u.user_id))
      var batch = { total: 0, done: 0, checked: 0, subscribed: 0 }
      users.forEach(user => { batch.total += this.enqueue('check', user, batch) })
    },

    releaseWaiting(userId) {
      var left = (this.waiting[userId] || 0) - 1
      if (left > 0) this.$set(this.waiting, userId, left)
      else this.$delete(this.waiting, userId)
    },

    settleBatch(batch) {
      if (!batch || batch.done < batch.total || !batch.checked) return
      this.notify(batch.subscribed ? 'success' : 'warning', this.$t('dash.boosting.invitations.check_summary', { subscribed: batch.subscribed, count: batch.checked }))
    },

    async runQueue() {
      if (this.processing) return
      this.processing = true
      while (this.actionQueue.length && !this.destroyed) {
        var action = this.actionQueue.shift()
        var userId = action.user.user_id
        this.releaseWaiting(userId)
        this.$set(this.busy, userId, true)
        var result = action.type === 'invite'
          ? await this.invite(action.user, action.pageId)
          : await this.check(action.user, action.pageId, !!action.batch)
        if (!this.waiting[userId]) this.$delete(this.busy, userId)
        if (action.batch) {
          action.batch.done++
          if (!result.error) action.batch.checked++
          if (result.subscribed) action.batch.subscribed++
        }
        if (STOP_ALL_ERRORS.includes(result.error)) this.cancelQueue(() => true)
        else if (STOP_INVITE_ERRORS.includes(result.error)) this.cancelQueue(a => a.type === 'invite')
        this.settleBatch(action.batch)
      }
      this.processing = false
    },

    cancelQueue(match) {
      var cancelled = this.actionQueue.filter(match)
      if (!cancelled.length) return
      this.actionQueue = this.actionQueue.filter(a => !match(a))
      cancelled.forEach(a => {
        this.releaseWaiting(a.user.user_id)
        if (!this.waiting[a.user.user_id]) this.$delete(this.busy, a.user.user_id)
        if (a.batch) a.batch.total--
      })
      this.notify('warning', this.$t('dash.boosting.invitations.queue_cancelled', { count: cancelled.length }))
    },

    async invite(user, pageId) {
      try {
        var { ok, json } = await this.api('POST', '/reporting/boosting/invite', { page_id: pageId, user_id: user.user_id })
        if (!ok) {
          this.notifyError(json)
          return { error: json.error || 'error' }
        }
        this.resolveEntry('pending', user, pageId)
        if (!json.skipped) this.schedule.invited_today++
        this.load('invited')
        this.$emit('changed')
        if (!json.skipped) this.notify('success', this.$t('dash.boosting.invitations.invited_notify', { name: user.user_name || user.user_id }))
        return { error: null }
      } catch (e) {
        this.notifyError({})
        return { error: 'error' }
      }
    },

    async inviteSelected() {
      this.bulkLoading = true
      try {
        var userIds = this.selection.pending.slice(0, this.bulkMax)
        var byPage = {}
        this.lists.pending.users
          .filter(u => userIds.includes(u.user_id))
          .forEach(u => this.actionableEntries(u, 'pending').forEach(entry => {
            byPage[entry.page_id] = (byPage[entry.page_id] || []).concat(u.user_id)
          }))
        var queued = 0
        for (var pageId of Object.keys(byPage)) {
          var { ok, json } = await this.api('POST', '/reporting/boosting/invite/bulk', { page_id: pageId, user_ids: byPage[pageId] })
          if (!ok) {
            this.notifyError(json)
            continue
          }
          queued += json.queued || 0
          byPage[pageId].forEach(id => this.$set(this.queued, id, true))
        }
        this.selection.pending = []
        if (queued) this.notify('success', this.$t('dash.boosting.invitations.queued_notify', { count: queued }), this.$t('dash.boosting.invitations.queued_notify_title'))
      } catch (e) {
        this.notifyError({})
      } finally {
        this.bulkLoading = false
      }
    },

    async check(user, pageId, quiet) {
      try {
        var { ok, json } = await this.api('POST', '/reporting/boosting/check', { page_id: pageId, user_id: user.user_id })
        if (!ok) {
          this.notifyError(json)
          return { error: json.error || 'error' }
        }
        var name = user.user_name || user.user_id
        if (json.subscribed) {
          this.resolveEntry('invited', user, pageId)
          this.load('subscribed')
          this.$emit('changed')
          if (!quiet) this.notify('success', this.$t('dash.boosting.invitations.subscribed_notify', { name }))
        } else {
          this.applyCheckResult(user, pageId, json.user)
          if (!quiet) this.notify('warning', this.$t('dash.boosting.invitations.not_subscribed_notify', { name }))
        }
        return { error: null, subscribed: !!json.subscribed }
      } catch (e) {
        this.notifyError({})
        return { error: 'error' }
      }
    },

    toggleAll() {
      this.selected = this.allSelected ? [] : this.selectableUsers.map(u => u.user_id)
    },

    notify(color, text, title) {
      this.$vs.notify({ title: title || this.$t('dash.tab.boosting'), text, color, position: 'top-right' })
    },

    notifyError(json) {
      var key = json && json.error
      var text = key && this.$te(key) ? this.$t(key) : this.$t('dash.boosting.invitations.error_generic')
      this.notify('danger', text, this.$t('dash.boosting.invitations.error_title'))
    },

    formatDate(date) {
      return date ? moment(date).format(this.dateFormat) : ''
    },

    formatDateTime(date) {
      return date ? moment(date).format(this.dateFormat + ' HH:mm') : ''
    },
  },
}
</script>

<style scoped>
.subsection-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 0 12px;
  font-size: 13px;
  font-weight: 600;
  color: #555E75;
}
.subsection-icon { font-size: 18px; color: #6B1F4D; }
.invitation-tabs { display: flex; gap: 4px; margin-bottom: 12px; flex-wrap: wrap; }
.ctab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 5.6px;
  cursor: pointer;
  font-size: 13px;
  color: #555e75;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
  user-select: none;
}
.ctab:hover { background: #f6f1fa; color: #6B1F4D; }
.ctab.active { background: #6B1F4D; color: #fff; }
.search-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #fdf9fc;
  border: 1px solid #e8e0f0;
  border-radius: 10px;
}
.search-icon { color: #c0a8d0; font-size: 18px; flex-shrink: 0; }
.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #121826;
  outline: none;
}
.search-input::placeholder { color: #c0a8d0; }
.search-clear { color: #c0a8d0; font-size: 16px; cursor: pointer; flex-shrink: 0; }
.search-clear:hover { color: #6B1F4D; }
.input-loader {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #e0d0ea;
  border-top-color: #6B1F4D;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}
.schedule-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  color: #555E75;
  margin: 0 0 8px;
}
.schedule-hint .material-icons { font-size: 16px; color: #6B1F4D; }
.schedule-quota { color: #9ba3b7; }
.disabled-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #555E75;
  background: #f9f7fb;
  border: 1px solid #f1edf5;
  border-radius: 5.6px;
  padding: 8px 12px;
  margin: 0 0 12px;
}
.disabled-hint .material-icons { font-size: 16px; color: #9ba3b7; }
.selection-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 4px 10px;
  border-bottom: 2px solid #f1edf5;
}
.select-all { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #555E75; cursor: pointer; }
input[type="checkbox"] { accent-color: #6B1F4D; width: 15px; height: 15px; cursor: pointer; flex-shrink: 0; }
input[type="checkbox"]:disabled { cursor: not-allowed; }
.subscriber-list { display: flex; flex-direction: column; }
.subscriber-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 4px;
  font-size: 13px;
  color: #121826;
  border-bottom: 1px solid #f1edf5;
}
.subscriber-avatar {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #f1edf5;
  color: #6B1F4D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.subscriber-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.subscriber-name {
  font-weight: 600;
  color: #121826;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}
a.subscriber-name:hover { color: #6B1F4D; }
.subscriber-meta { font-size: 12px; color: #9ba3b7; }
.subscriber-post {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  font-size: 12px;
  color: #6B1F4D;
  text-decoration: none;
}
.subscriber-post:hover { text-decoration: underline; }
.subscriber-post .material-icons { font-size: 14px; }
.type-badge {
  display: inline-flex; align-items: center;
  padding: 1px 7px; border-radius: 10px;
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.2px; white-space: nowrap; max-width: fit-content;
  flex-shrink: 0;
}
.badge-pending { background: #fdf4ff; color: #6B1F4D; }
.badge-error { background: #fdf2f2; color: #c0392b; }
.badge-cache { background: #f1f5f9; color: #9ba3b7; }
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea;
  background: #fff;
  color: #6B1F4D;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.action-btn:hover:not(:disabled) { background: #f1edf5; border-color: #6B1F4D; }
.action-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.action-btn .material-icons { font-size: 15px; }
.action-btn--primary { background: #6B1F4D; border-color: #6B1F4D; color: #fff; }
.action-btn--primary:hover:not(:disabled) { background: #6B1F4D; opacity: 0.88; }
.post-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 30px 0;
  color: #9ba3b7;
}
.post-empty .material-icons { font-size: 40px; color: #e0d0ea; }
.post-empty p { font-size: 14px; }
.page-btn {
  display: flex; align-items: center; justify-content: center;
  height: 30px; border-radius: 8px;
  border: 1px solid #e0d0ea; background: #fff; color: #6B1F4D;
  cursor: pointer; transition: all 0.15s;
}
.page-btn:hover:not(:disabled) { background: #f1edf5; }
.page-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.see-more { align-self: center; margin-top: 12px; padding: 0 16px; font-size: 13px; }
.spin { animation: spin 2s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
